/**
 * Web 版のヘルプ（タスク50・構成はタスク51）。右上ツールバーの「?」から開く。
 *
 * - タブで「地図の見方／参加する／プライバシー」に分ける（見出し＋箇条書きの縦並びは文字の壁になったため）。
 * - 地図の見方は、地図上の実物と同じ見た目の小さなアイコン＋名前＋一言の凡例（helpModal.css で再現）。
 * - 参加するは番号付きの手順、プライバシーは表示される期間の表。
 *
 * ツールバーは MapLibre のコントロール枠（z-index:2）の中に描かれるため、そのまま描くと左上の公開中パネルや
 * 左下メニュー（z-index:4）より下になり得る。createPortal で document.body に描いて最前面に出す。
 * 閉じるのは「閉じる」ボタン・背景クリック・ESC（ESC 時のフォーカス戻しは呼び出し側の onClose が担当）。
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import './helpModal.css';

type HelpTab = 'map' | 'join' | 'privacy';
const TABS: HelpTab[] = ['map', 'join', 'privacy'];

/** 凡例のアイコン。地図上の実物（pinStyle.css / postLayer.css / Trail.ts）と同じ見た目を CSS で再現する。 */
function LegendIcon({ kind }: { kind: string }) {
  switch (kind) {
    case 'participant':
      return <span className="help-icon help-icon--avatar">A</span>;
    case 'pulse':
      return (
        <span className="help-icon help-icon--pulse-wrap">
          <span className="help-icon__pulse" />
          <span className="help-icon help-icon--avatar">A</span>
        </span>
      );
    case 'self':
      return <span className="help-icon help-icon--avatar help-icon--self">A</span>;
    case 'trail':
      return <span className="help-icon help-icon--trail" />;
    case 'cluster':
      return <span className="help-icon help-icon--cluster">3</span>;
    case 'status':
      return <span className="help-icon help-icon--status">…</span>;
    case 'venue':
      return <span className="help-icon help-icon--venue">🏰</span>;
    case 'post':
      return <span className="help-icon help-icon--post">💬</span>;
    case 'postCluster':
      return <span className="help-icon help-icon--post-cluster">4</span>;
    default:
      return <span className="help-icon" />;
  }
}

export interface HelpModalProps {
  readonly lang: Lang;
  readonly onClose: () => void;
}

export function HelpModal({ lang, onClose }: HelpModalProps) {
  const m = messages(lang);
  const h = m.help;
  const [tab, setTab] = useState<HelpTab>('map');

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="help-modal__overlay" onClick={onClose}>
      <div
        className="help-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={h.title}
      >
        <h2 className="help-modal__title">{h.title}</h2>

        <div className="help-modal__tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`help-tab-${t}`}
              aria-selected={tab === t}
              aria-controls={`help-panel-${t}`}
              className={`help-modal__tab${tab === t ? ' help-modal__tab--active' : ''}`}
              onClick={() => setTab(t)}
            >
              {h.tabs[t]}
            </button>
          ))}
        </div>

        <div
          className="help-modal__panel"
          role="tabpanel"
          id={`help-panel-${tab}`}
          aria-labelledby={`help-tab-${tab}`}
        >
          {tab === 'map' && (
            <ul className="help-legend">
              {h.legend.map((item) => (
                <li key={item.key} className="help-legend__row">
                  <span className="help-legend__icon">
                    <LegendIcon kind={item.key} />
                  </span>
                  <span className="help-legend__text">
                    <strong>{item.name}</strong>
                    <span>{item.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {tab === 'join' && (
            <>
              <ol className="help-steps">
                {h.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <p className="help-modal__aside">{h.telegram}</p>
            </>
          )}

          {tab === 'privacy' && (
            <>
              <table className="help-table">
                <thead>
                  <tr>
                    <th scope="col">{h.privacyHeader.item}</th>
                    <th scope="col">{h.privacyHeader.period}</th>
                  </tr>
                </thead>
                <tbody>
                  {h.privacyRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="help-notes">
                {h.privacyNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </>
          )}
        </div>

        <button type="button" className="help-modal__close" onClick={onClose}>
          {m.common.close}
        </button>
      </div>
    </div>,
    document.body,
  );
}
